/**
 * Ringover panel : click-to-call + call history + transcription
 * Displayed on each dossier detail page.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Loader2, Sparkles, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}
function fmtDuration(sec) {
  const s = Math.round(sec || 0);
  if (!s) return "—";
  const m = Math.floor(s / 60), r = s % 60;
  return m > 0 ? `${m}m${r.toString().padStart(2, "0")}s` : `${r}s`;
}

function CallRow({ call, onTranscribe, expanded, onToggleExpand }) {
  const dir = call.direction === "out" || call.direction === "OUT" || call.type === "outgoing" ? "out" : "in";
  const answered = call.is_answered !== false && call.answered_time !== null;
  const state = (call.last_state || "").toUpperCase();
  const missed = state === "MISSED" || state === "VOICEMAIL" || !answered;
  const Icon = missed ? PhoneMissed : dir === "out" ? PhoneOutgoing : PhoneIncoming;
  const colorClass = missed ? "text-orange-600" : dir === "out" ? "text-blue-600" : "text-emerald-600";

  const cid = String(call.call_id || call.id || call.cdr_id || "");
  const otherNumber = dir === "out" ? call.to_number : call.from_number;
  const hasRecord = !!(call.record || call.recording_url);
  const hasTranscript = !!call._transcript;

  return (
    <div className="border-b border-norea-border last:border-0" data-testid={`ringover-call-${cid}`}>
      <div className="flex items-center gap-3 py-2.5 px-3 hover:bg-norea-surfaceHover text-sm">
        <Icon className={`w-4 h-4 flex-shrink-0 ${colorClass}`} />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-norea-text truncate">+{otherNumber}</div>
          <div className="text-[11px] text-norea-textMuted">
            {fmtDate(call.start_time || call.start_date)} · {fmtDuration(call.total_duration || call.duration)}
            {call.user?.firstname && <> · {call.user.firstname} {call.user.lastname}</>}
          </div>
        </div>
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
          missed ? "bg-orange-50 text-orange-700 border border-orange-200" :
          "bg-emerald-50 text-emerald-700 border border-emerald-200"
        }`}>
          {missed ? "Manqué" : "Répondu"}
        </span>
        {hasRecord && (
          hasTranscript ? (
            <button
              onClick={() => onToggleExpand(cid)}
              className="text-[11px] px-2 py-1 rounded border border-norea-border hover:bg-norea-surfaceHover inline-flex items-center gap-1"
              data-testid={`ringover-view-transcript-${cid}`}
            >
              <Sparkles className="w-3 h-3 text-norea-primary" />
              Transcript
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          ) : (
            <button
              onClick={() => onTranscribe(cid)}
              className="text-[11px] px-2 py-1 rounded border border-norea-border hover:bg-norea-surfaceHover inline-flex items-center gap-1"
              data-testid={`ringover-transcribe-${cid}`}
            >
              <Sparkles className="w-3 h-3" />
              Transcrire
            </button>
          )
        )}
      </div>
      {expanded && hasTranscript && (
        <div className="px-3 pb-3 bg-norea-bg space-y-2">
          {call._summary && (
            <div className="p-2 rounded bg-norea-primary/5 border border-norea-primary/20 text-xs whitespace-pre-wrap">
              <div className="font-semibold text-norea-primary mb-1 uppercase tracking-wider text-[10px]">Résumé IA</div>
              {call._summary}
            </div>
          )}
          <div className="p-2 rounded bg-white border border-norea-border text-xs whitespace-pre-wrap max-h-[240px] overflow-y-auto">
            <div className="font-semibold text-norea-textMuted mb-1 uppercase tracking-wider text-[10px]">Transcription</div>
            {call._transcript}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DossierRingover({ dossierId, phones }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [dialing, setDialing] = useState(false);
  const [transcribingId, setTranscribingId] = useState(null);
  const [customPhone, setCustomPhone] = useState("");
  const [latestCall, setLatestCall] = useState(null);

  const load = useCallback(async () => {
    if (!dossierId) return;
    setLoading(true);
    try {
      const r = await api.get(`/ringover/calls?dossier_id=${encodeURIComponent(dossierId)}&limit_count=50`);
      if (r.data?.ok) {
        setCalls(r.data.calls || []);
        setLatestCall(r.data.latest_account_call || null);
      } else {
        setCalls([]);
        if (r.data?.error) toast.error(r.data.error);
      }
    } catch (e) {
      // silent — Ringover may not be configured yet
    } finally {
      setLoading(false);
    }
  }, [dossierId]);

  useEffect(() => { load(); }, [load]);

  const callNumber = async (numToCall) => {
    if (!numToCall) return toast.error("Numéro manquant");
    setDialing(true);
    try {
      const r = await api.post("/ringover/click-to-call", {
        to_number: numToCall,
        dossier_id: dossierId,
      });
      if (r.data?.ok) {
        toast.success("Ringover va faire sonner votre poste puis composer le numéro");
      } else {
        toast.error(r.data?.error || `Ringover a refusé (HTTP ${r.data?.http_status})`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 400 && typeof detail === "string") {
        toast.error(detail);
      } else if (status) {
        toast.error(`Erreur ${status} — ${typeof detail === "string" ? detail : "voir logs backend"}`);
      } else {
        toast.error("Réseau injoignable");
      }
    } finally {
      setDialing(false);
    }
  };

  const transcribe = async (callId) => {
    setTranscribingId(callId);
    try {
      const r = await api.post(`/ringover/calls/${encodeURIComponent(callId)}/transcribe`);
      if (r.data?.ok) {
        // update in place
        setCalls((cs) => cs.map((c) => {
          const cid = String(c.call_id || c.id || c.cdr_id || "");
          if (cid === callId) return { ...c, _transcript: r.data.transcript, _summary: r.data.summary };
          return c;
        }));
        setExpanded((e) => ({ ...e, [callId]: true }));
        toast.success(r.data.cached ? "Transcription déjà générée" : "Transcription générée");
      } else {
        toast.error(r.data?.error || "Transcription impossible");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transcription impossible");
    } finally {
      setTranscribingId(null);
    }
  };

  const primaryPhone = phones?.[0] || "";

  return (
    <section className="bg-white border border-norea-border rounded-lg overflow-hidden h-full flex flex-col min-w-0" data-testid="dossier-ringover-panel">
      <div className="px-4 py-3 border-b border-norea-border bg-norea-bg flex items-center gap-3">
        <Phone className="w-4 h-4 text-norea-primary" />
        <div className="flex-1">
          <div className="font-semibold text-sm text-norea-text">Téléphonie Ringover</div>
          <div className="text-[11px] text-norea-textMuted">
            {phones?.length > 0 ? `${phones.length} numéro(s) rattaché(s) à ce dossier` : "Aucun numéro sur ce dossier"}
          </div>
          {latestCall && (
            <div className="text-[10px] text-norea-textMuted" data-testid="ringover-latest-call">
              Dernier appel reçu de Ringover (tout le compte) : {new Date(latestCall).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} · transcription auto toutes les 10 min
            </div>
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-xs px-2 py-1 rounded border border-norea-border hover:bg-norea-surfaceHover inline-flex items-center gap-1"
          data-testid="ringover-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Actualiser
        </button>
      </div>

      {/* Click-to-call — uses tel: links so the Ringover Chrome Dialer extension can detect them and dial directly (no callback loop). */}
      <div className="px-4 py-3 bg-white border-b border-norea-border">
        <div className="flex items-center gap-2 flex-wrap">
          {primaryPhone && (
            <a
              href={`tel:${primaryPhone}`}
              className="text-sm px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-2 no-underline"
              data-testid="ringover-call-primary"
            >
              <Phone className="w-4 h-4" />
              Appeler {primaryPhone}
            </a>
          )}
          {(phones || []).slice(1).map((p) => (
            <a
              key={p}
              href={`tel:${p}`}
              className="text-xs px-2 py-1.5 rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-50 no-underline"
              data-testid={`ringover-call-${p}`}
            >
              📞 {p}
            </a>
          ))}
          <div className="flex items-center gap-1 ml-auto flex-shrink-0">
            <input
              type="tel"
              value={customPhone}
              onChange={(e) => setCustomPhone(e.target.value)}
              placeholder="Autre numéro…"
              className="text-xs px-2 py-1.5 border border-norea-border rounded outline-none focus:border-norea-primary w-32"
              data-testid="ringover-custom-number"
            />
            <a
              href={customPhone.trim() ? `tel:${customPhone.trim()}` : undefined}
              onClick={(e) => { if (!customPhone.trim()) e.preventDefault(); }}
              className={`text-xs px-2 py-1.5 rounded border border-norea-border no-underline ${customPhone.trim() ? "hover:bg-norea-surfaceHover text-norea-text" : "opacity-40 pointer-events-none"}`}
              data-testid="ringover-call-custom"
            >
              Appeler
            </a>
          </div>
        </div>
        <div className="mt-2 text-[11px] text-norea-textMuted">
          💡 L'appel se déclenche via <a href="https://chromewebstore.google.com/detail/ringover-dialer/jjikmbicpomagidbcllljdgpphbelmmi" target="_blank" rel="noreferrer" className="text-norea-primary hover:underline">l'extension Chrome Ringover Dialer</a> (à installer une seule fois). L'historique et la transcription ci-dessous continuent de venir de l'API Ringover.
        </div>
      </div>

      {/* Call log */}
      <div className="flex-1 max-h-[320px] overflow-y-auto" data-testid="ringover-call-log">
        {loading && calls.length === 0 ? (
          <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-norea-primary mx-auto" /></div>
        ) : calls.length === 0 ? (
          <div className="p-6 text-center text-xs text-norea-textMuted italic">
            Aucun appel trouvé pour ces numéros. Passez un premier appel pour commencer l'historique.
          </div>
        ) : (
          calls.map((c) => (
            <CallRow
              key={String(c.call_id || c.id || c.cdr_id)}
              call={c}
              onTranscribe={transcribingId ? () => {} : transcribe}
              expanded={!!expanded[String(c.call_id || c.id || c.cdr_id)]}
              onToggleExpand={(cid) => setExpanded((e) => ({ ...e, [cid]: !e[cid] }))}
            />
          ))
        )}
      </div>
    </section>
  );
}
